/**
 * CategoryDrilldown - inline drill-down for a donut slice / legend row
 *
 * Opens under the Spending by Category card when a category is clicked:
 * period stats (total, share, count, monthly average), the largest
 * transactions in the range, and a "View in History" deep link
 * (/transactions?categoryId=...).
 *
 * Mirrors the donut's numbers exactly: adjustments and capital-move
 * transactions are excluded via the same matcher the page passed to
 * useAnalytics. "Uncategorized" covers txns with a missing OR orphaned
 * categoryId (same bucketing as useAnalytics).
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrivacyMode } from '@hooks/usePrivacyMode';
import { transactionService } from '@services/transactions';
import { db, type Transaction } from '@services/db';
import { formatCurrency as formatCurrencyIntl, formatMonthDay } from '@/utils/format';
import { useLanguage, categoryDisplayName } from '@/i18n';
import './CategoryDrilldown.css';

export interface DrilldownCategory {
  categoryId: string;
  categoryName: string;
  categoryColor: string;
  amount: number;
  percentage: number;
  transactionCount?: number;
  categoryIds?: string[];
  isUncategorized?: boolean;
}

interface CategoryDrilldownProps {
  category: DrilldownCategory;
  dateRange: { start: Date; end: Date; label: string };
  /** Same matcher useAnalytics used - keeps drill-down totals consistent */
  capitalMoveMatcher: ((description: string) => boolean) | null;
  onClose: () => void;
}

const TOP_TXNS_SHOWN = 8;

function isAdjustment(desc: string | undefined): boolean {
  if (!desc) return false;
  const lower = desc.toLowerCase();
  return lower.includes('[adjustment]') || lower.startsWith('adjustment');
}

function fmt(amount: number): string {
  return formatCurrencyIntl(amount, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function Amount({ value, privacy }: { value: number; privacy: boolean }) {
  if (privacy) return <span className="private-amount masked">••••</span>;
  return <>{fmt(value)}</>;
}

export function CategoryDrilldown({
  category,
  dateRange,
  capitalMoveMatcher,
  onClose,
}: CategoryDrilldownProps) {
  const { t } = useLanguage();
  const { isPrivacyMode } = usePrivacyMode();
  const navigate = useNavigate();
  const [txns, setTxns] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  const rangeStartMs = dateRange.start.getTime();
  const rangeEndMs = dateRange.end.getTime();
  const idsKey = (category.categoryIds || [category.categoryId]).join(',');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        const [all, categories] = await Promise.all([
          transactionService.getByDateRange(new Date(rangeStartMs), new Date(rangeEndMs)),
          db.categories.toArray(),
        ]);
        if (cancelled) return;

        const wanted = new Set(category.categoryIds || [category.categoryId]);
        const validIds = new Set(categories.map((c) => c.id));

        const matched = all.filter((txn) => {
          if (txn.type !== 'expense' || txn.deletedAt) return false;
          if (isAdjustment(txn.description)) return false;
          if (capitalMoveMatcher && capitalMoveMatcher(txn.description)) return false;
          if (category.isUncategorized) {
            return !txn.categoryId || !validIds.has(txn.categoryId);
          }
          return !!txn.categoryId && wanted.has(txn.categoryId);
        });

        matched.sort((a, b) => b.amount - a.amount);
        setTxns(matched);
      } catch (err) {
        console.error('[CategoryDrilldown] Error:', err);
        if (!cancelled) setTxns([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStartMs, rangeEndMs, idsKey, category.isUncategorized, capitalMoveMatcher]);

  const stats = useMemo(() => {
    const total = txns.reduce((sum, x) => sum + x.amount, 0);
    const months = Math.max(1, (rangeEndMs - rangeStartMs) / 86400000 / 30.4375);
    return {
      total,
      count: txns.length,
      avg: txns.length > 0 ? total / txns.length : 0,
      monthlyAvg: total / months,
    };
  }, [txns, rangeStartMs, rangeEndMs]);

  // History deep link works for a single real category id only
  // (aggregates like "Other categories" and Uncategorized have no single id)
  const singleId =
    !category.isUncategorized &&
    category.categoryId !== '__other_categories__' &&
    (category.categoryIds || [category.categoryId]).length === 1
      ? (category.categoryIds || [category.categoryId])[0]
      : null;

  return (
    <div className="drilldown-card" style={{ borderLeftColor: category.categoryColor }}>
      <div className="drilldown-head">
        <span className="drilldown-dot" style={{ background: category.categoryColor }} />
        <span className="drilldown-title">{categoryDisplayName(category.categoryName)}</span>
        <span className="drilldown-share">
          {t('analytics.drilldown.share', { percent: category.percentage.toFixed(0) })}
        </span>
        <button className="drilldown-close" onClick={onClose} aria-label={t('common.close')}>
          ×
        </button>
      </div>

      {loading ? (
        <div className="drilldown-loading">{t('common.loading')}</div>
      ) : (
        <>
          <div className="drilldown-stats">
            <div className="drilldown-stat">
              <span className="drilldown-stat-label">{t('analytics.drilldown.total')}</span>
              <span className="drilldown-stat-value amount--expense">
                <Amount value={stats.total} privacy={isPrivacyMode} />
              </span>
            </div>
            <div className="drilldown-stat">
              <span className="drilldown-stat-label">{t('analytics.drilldown.txns')}</span>
              <span className="drilldown-stat-value">{stats.count}</span>
            </div>
            <div className="drilldown-stat">
              <span className="drilldown-stat-label">{t('analytics.drilldown.avg_txn')}</span>
              <span className="drilldown-stat-value">
                <Amount value={stats.avg} privacy={isPrivacyMode} />
              </span>
            </div>
            <div className="drilldown-stat">
              <span className="drilldown-stat-label">{t('analytics.drilldown.monthly_avg')}</span>
              <span className="drilldown-stat-value">
                <Amount value={stats.monthlyAvg} privacy={isPrivacyMode} />
              </span>
            </div>
          </div>

          {txns.length > 0 && (
            <div className="drilldown-txns">
              <div className="drilldown-txns-title">{t('analytics.drilldown.largest')}</div>
              {txns.slice(0, TOP_TXNS_SHOWN).map((txn) => (
                <div key={txn.id} className="drilldown-txn-row">
                  <span className="drilldown-txn-desc">
                    {txn.description || t('transactions.upcoming.no_description')}
                  </span>
                  <span className="drilldown-txn-date">
                    {formatMonthDay(txn.date instanceof Date ? txn.date : new Date(txn.date))}
                  </span>
                  <span className="drilldown-txn-amount amount--expense">
                    <Amount value={txn.amount} privacy={isPrivacyMode} />
                  </span>
                </div>
              ))}
              {txns.length > TOP_TXNS_SHOWN && (
                <div className="drilldown-more">
                  {t('analytics.drilldown.more', { count: txns.length - TOP_TXNS_SHOWN })}
                </div>
              )}
            </div>
          )}

          {singleId && (
            <div className="drilldown-footer">
              <button
                className="drilldown-history-btn"
                onClick={() => navigate(`/transactions?categoryId=${encodeURIComponent(singleId)}`)}
              >
                {t('analytics.drilldown.view_history')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CategoryDrilldown;
